<template>
  <main class="marketing-page">
    <section class="marketing-shell">
      <header class="marketing-header">
        <p class="marketing-eyebrow">Marketing</p>
        <h1>Physical Marketing</h1>
        <p class="marketing-subtitle">
          Design business cards, flyers, and other print materials, then export a print-ready PDF.
        </p>
      </header>

      <div v-if="pageError" class="banner banner-error">{{ pageError }}</div>

      <!-- ── Gallery mode ── -->
      <template v-if="mode === 'gallery'">
        <section class="panel">
          <div class="panel-header">
            <h2>My Designs</h2>
          </div>

          <div v-if="materialsLoading" class="empty-state">
            <v-progress-circular indeterminate size="24" color="primary" />
            <span>Loading…</span>
          </div>

          <div v-else-if="materials.length" class="materials-grid">
            <div v-for="m in materials" :key="m.id" class="material-card">
              <div class="material-thumb" :style="thumbStyle(m.thumbnail)">
                <v-icon v-if="!m.thumbnail" size="30">mdi-card-account-mail-outline</v-icon>
              </div>
              <div class="material-body">
                <strong>{{ m.title }}</strong>
                <span>{{ m.template_name }}</span>
              </div>
              <div class="material-actions">
                <button class="row-action" type="button" @click="openMaterial(m.id)">Edit</button>
                <button class="row-action" type="button" :disabled="exportingId === m.id" @click="exportPdf(m)">
                  <v-progress-circular v-if="exportingId === m.id" indeterminate size="13" width="2" />
                  <template v-else>Export PDF</template>
                </button>
                <button class="row-action row-action--danger" type="button" @click="deleteMaterial(m)">Delete</button>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <v-icon size="28">mdi-card-account-mail-outline</v-icon>
            <span>No designs yet — start one from a template below.</span>
          </div>
        </section>

        <section class="panel">
          <div class="panel-header">
            <h2>Start a New Design</h2>
          </div>

          <div v-if="templatesLoading" class="empty-state">
            <v-progress-circular indeterminate size="24" color="primary" />
            <span>Loading templates…</span>
          </div>

          <div v-else-if="templates.length" class="templates-grid">
            <button
              v-for="t in templates"
              :key="t.id"
              class="template-card"
              type="button"
              :disabled="creatingFromTemplate === t.id"
              @click="createFromTemplate(t)"
            >
              <div class="material-thumb" :style="thumbStyle(t.thumbnail)">
                <v-icon v-if="!t.thumbnail" size="30">mdi-file-outline</v-icon>
              </div>
              <strong>{{ t.name }}</strong>
              <span>{{ t.width_in }}in × {{ t.height_in }}in</span>
              <span v-if="creatingFromTemplate === t.id" class="template-creating">Creating…</span>
            </button>
          </div>

          <div v-else class="empty-state">
            <v-icon size="28">mdi-file-outline</v-icon>
            <span>No print templates are available yet.</span>
          </div>
        </section>
      </template>

      <!-- ── Editor mode ── -->
      <template v-else-if="mode === 'editor' && activeMaterial">
        <div class="editor-toolbar">
          <button class="row-action" type="button" @click="backToGallery">
            <v-icon size="15">mdi-arrow-left</v-icon> Back to Designs
          </button>
          <div class="editor-toolbar-actions">
            <button class="row-action" type="button" :disabled="exportingId === activeMaterial.id" @click="exportPdf(activeMaterial)">
              <v-progress-circular v-if="exportingId === activeMaterial.id" indeterminate size="13" width="2" />
              <template v-else><v-icon size="15">mdi-tray-arrow-down</v-icon> Export PDF</template>
            </button>
            <button class="admin-action-primary" type="button" :disabled="saving" @click="saveMaterial">
              <v-progress-circular v-if="saving" indeterminate size="15" width="2" />
              <template v-else-if="saveSuccess">Saved</template>
              <template v-else>Save</template>
            </button>
          </div>
        </div>

        <div class="editor-layout">
          <div class="editor-fields">
            <label class="field">
              <span>Design Title</span>
              <input class="form-field" type="text" v-model="materialTitle" />
            </label>

            <div v-for="(section, sectionKey) in editableSections" :key="sectionKey" class="editor-section">
              <p class="editor-section-title">{{ sectionLabel(sectionKey) }}</p>
              <FieldRenderer
                v-for="(field, fieldKey) in section"
                :key="fieldKey"
                :section-key="sectionKey"
                :field-key="fieldKey"
                :field="field"
                :content="materialContent"
                @image-pick="handleImagePickFromField"
              />
            </div>
          </div>

          <div class="editor-preview">
            <p class="editor-preview-label">
              Preview ({{ activeTemplate?.width_in }}in × {{ activeTemplate?.height_in }}in)
            </p>
            <div class="editor-preview-canvas" :style="previewCanvasStyle">
              <component :is="previewComponent" v-if="previewComponent" :preview-content="materialContent" />
              <component :is="'style'" :key="previewCssKey">{{ activeTemplate?.css }}</component>
            </div>
          </div>
        </div>
      </template>
    </section>

    <ImagePickerModal
      v-model="imagePickerOpen"
      :images="tenantImages.images.value"
      :loading="tenantImages.loading.value"
      :uploading="tenantImages.uploading.value"
      :upload-error="tenantImages.uploadError.value"
      :selected-url="imagePickerSelectedUrl"
      :deletable="true"
      :deleting="tenantImages.deleting.value"
      title="Choose image"
      subtitle="Select or upload an image."
      @select="handleImageSelect"
      @upload="handleImageUpload"
      @delete="tenantImages.remove"
    />
  </main>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import FieldRenderer from '@/components/builder/FieldRenderer.vue'
import ImagePickerModal from '@/components/builder/ImagePickerModal.vue'
import { compilePreviewComponent } from '@/utils/templatePreview'
import { useTenantImages } from '@/composables/useTenantImages'

const mode = ref('gallery')
const pageError = ref('')

const materials = ref([])
const materialsLoading = ref(false)

const templates = ref([])
const templatesLoading = ref(false)
const creatingFromTemplate = ref(null)

const activeMaterial = ref(null)
const activeTemplate = ref(null)
const materialTitle = ref('')
const materialContent = ref({})
const saving = ref(false)
const saveSuccess = ref(false)
const exportingId = ref(null)
const previewCssKey = ref(0)

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchMaterials() {
  materialsLoading.value = true
  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/print-materials?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to load designs.')
    materials.value = data.materials || []
  } catch (err) {
    pageError.value = err.message || 'Failed to load designs.'
  } finally {
    materialsLoading.value = false
  }
}

async function fetchTemplates() {
  templatesLoading.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/print-templates`)
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || 'Failed to load print templates.')
    templates.value = data.templates || []
  } catch (err) {
    pageError.value = err.message || 'Failed to load print templates.'
  } finally {
    templatesLoading.value = false
  }
}

watchEffect(() => {
  if (company.company_id) fetchMaterials()
})
fetchTemplates()

function thumbStyle(url) {
  return url ? { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}
}

async function createFromTemplate(template) {
  creatingFromTemplate.value = template.id
  pageError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/print-materials`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_id: company.company_id,
        print_template_id: template.id,
        title: `${template.name} — ${new Date().toLocaleDateString('en-CA')}`,
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to create design.')

    const token = await getAuthToken()
    logAction(token, {
      action: 'marketing.print-material-create',
      label: `Created print design "${data.material.title}"`,
      company_id: company.company_id,
    })

    await fetchMaterials()
    loadMaterialIntoEditor(data.material)
  } catch (err) {
    pageError.value = err.message || 'Failed to create design.'
  } finally {
    creatingFromTemplate.value = null
  }
}

async function openMaterial(id) {
  pageError.value = ''
  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/print-materials/${id}`, { headers })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to load design.')
    loadMaterialIntoEditor(data.material)
  } catch (err) {
    pageError.value = err.message || 'Failed to load design.'
  }
}

function loadMaterialIntoEditor(material) {
  activeMaterial.value = material
  activeTemplate.value = {
    id: material.print_template_id,
    width_in: material.width_in,
    height_in: material.height_in,
    css: material.template_css,
  }
  materialTitle.value = material.title
  materialContent.value = mergeWithTemplateDefaults(material.template_content, material.content)
  previewCssKey.value++
  mode.value = 'editor'
}

function mergeWithTemplateDefaults(templateContent, savedContent) {
  const base = JSON.parse(JSON.stringify(templateContent || {}))
  const saved = savedContent || {}

  for (const sectionKey of Object.keys(saved)) {
    if (!base[sectionKey]) base[sectionKey] = {}
    for (const fieldKey of Object.keys(saved[sectionKey] || {})) {
      base[sectionKey][fieldKey] = { ...(base[sectionKey][fieldKey] || {}), ...saved[sectionKey][fieldKey] }
    }
  }

  return base
}

function backToGallery() {
  mode.value = 'gallery'
  activeMaterial.value = null
  activeTemplate.value = null
}

function sectionLabel(key) {
  return key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const editableSections = computed(() => {
  const sections = {}
  for (const [sectionKey, section] of Object.entries(materialContent.value || {})) {
    if (!section || typeof section !== 'object') continue
    const fields = Object.fromEntries(
      Object.entries(section).filter(([key, value]) => key !== '_meta' && value?.type !== 'hidden')
    )
    if (Object.keys(fields).length) sections[sectionKey] = fields
  }
  return sections
})

// activeMaterial is loaded via the joined GET /print-materials/:id route, so
// its template markup comes along for free — no separate template fetch needed.
const previewComponent = computed(() => {
  const markup = activeMaterial.value?.template_markup
  if (!markup) return null
  try {
    return compilePreviewComponent(markup)
  } catch {
    return null
  }
})

const previewCanvasStyle = computed(() => ({
  width: `${activeTemplate.value?.width_in || 3.5}in`,
  height: `${activeTemplate.value?.height_in || 2}in`,
}))

async function saveMaterial() {
  if (!activeMaterial.value) return
  saving.value = true
  saveSuccess.value = false

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/print-materials/${activeMaterial.value.id}`, {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: materialTitle.value, content: materialContent.value }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to save design.')

    activeMaterial.value = data.material
    saveSuccess.value = true

    const token = await getAuthToken()
    logAction(token, {
      action: 'marketing.print-material-save',
      label: `Saved print design "${materialTitle.value}"`,
      company_id: company.company_id,
    })

    setTimeout(() => { saveSuccess.value = false }, 3000)
    await fetchMaterials()
  } catch (err) {
    pageError.value = err.message || 'Failed to save design.'
  } finally {
    saving.value = false
  }
}

async function exportPdf(material) {
  exportingId.value = material.id
  pageError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/print-materials/${material.id}/pdf`, { headers })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to export PDF.')
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${(material.title || 'design').replace(/[^a-z0-9-_ ]/gi, '')}.pdf`
    link.click()
    URL.revokeObjectURL(url)

    const token = await getAuthToken()
    logAction(token, {
      action: 'marketing.print-material-export',
      label: `Exported PDF for "${material.title}"`,
      company_id: company.company_id,
    })
  } catch (err) {
    pageError.value = err.message || 'Failed to export PDF.'
  } finally {
    exportingId.value = null
  }
}

async function deleteMaterial(material) {
  if (!confirm(`Delete the design "${material.title}"?`)) return

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/print-materials/${material.id}`, {
      method: 'DELETE',
      headers,
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to delete design.')
    await fetchMaterials()
  } catch (err) {
    pageError.value = err.message || 'Failed to delete design.'
  }
}

// ── Image picker wiring (same pattern as BuilderView.vue, via the shared
// useTenantImages composable — see src/composables/useTenantImages.js) ─────
const tenantImages = useTenantImages()
const imagePickerOpen = ref(false)
const imagePickerFieldTarget = ref(null)
const imagePickerSelectedUrl = ref('')

function handleImagePickFromField({ sectionKey, fieldKey }) {
  imagePickerFieldTarget.value = { sectionKey, fieldKey }
  imagePickerSelectedUrl.value = materialContent.value[sectionKey]?.[fieldKey]?.value || ''
  imagePickerOpen.value = true
  tenantImages.load(company.company_id)
}

function handleImageSelect(image) {
  if (!image?.url || !imagePickerFieldTarget.value) return
  const { sectionKey, fieldKey } = imagePickerFieldTarget.value
  if (!materialContent.value[sectionKey]) materialContent.value[sectionKey] = {}
  if (!materialContent.value[sectionKey][fieldKey]) {
    materialContent.value[sectionKey][fieldKey] = { type: 'image', label: fieldKey, value: '' }
  }
  materialContent.value[sectionKey][fieldKey].value = image.url
}

async function handleImageUpload(file) {
  const image = await tenantImages.upload(company.company_id, file)
  if (image) {
    handleImageSelect(image)
    imagePickerOpen.value = false
  }
}
</script>

<style scoped>
.marketing-page {
  min-height: calc(100dvh - 70px);
  padding: 32px 18px 110px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.marketing-shell {
  width: min(1100px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

.marketing-eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.marketing-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.marketing-subtitle {
  margin: 8px 0 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
}

.banner {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 20px;
}

.panel-header h2 {
  margin: 0 0 16px;
  font-size: 1.1rem;
  font-weight: 800;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.materials-grid,
.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
}

.material-card,
.template-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.template-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.material-thumb {
  height: 100px;
  border-radius: 6px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  margin-bottom: 6px;
}

.material-body {
  display: flex;
  flex-direction: column;
}

.material-body span,
.template-card span {
  font-size: 0.78rem;
  color: var(--text-secondary);
}

.material-actions {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.template-creating {
  font-weight: 700;
  color: #1f5c4d;
}

.row-action {
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.row-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.row-action--danger {
  color: #8a2b1f;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.editor-toolbar-actions {
  display: flex;
  gap: 10px;
}

.admin-action-primary {
  min-width: 90px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;
}

.admin-action-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.editor-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 20px;
  align-items: start;
}

.editor-fields {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 20px;
  display: grid;
  gap: 18px;
}

.editor-section {
  display: grid;
  gap: 12px;
  padding-top: 14px;
  border-top: 1px solid var(--border-subtle);
}

.editor-section-title {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 800;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.field {
  display: grid;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-secondary);
}

.form-field {
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.editor-preview {
  position: sticky;
  top: 12px;
  display: grid;
  gap: 8px;
  justify-items: center;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 20px;
}

.editor-preview-label {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-align: center;
}

.editor-preview-canvas {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border-subtle);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(23, 35, 31, 0.1);
  max-width: 100%;
}

@media (max-width: 780px) {
  .editor-layout {
    grid-template-columns: 1fr;
  }

  .editor-preview {
    position: static;
  }
}

@media (max-width: 620px) {
  .marketing-page {
    padding: 22px 14px 110px;
  }
}
</style>
